/**
 * Benchmark chart data for the /benchmarks page.
 * Every number here traces to a reproducible source — see each entry's
 * `methodology` block. Add new comparisons as additional array entries;
 * once this list grows past ~5-6 entries, promote the "Benchmarks" nav
 * link to a dropdown (see HeaderMenu.jsx's DropdownMenu/ProductColumn).
 */

export const GROUP_COLORS = {
    aihumanity: { fill: "#6ddcff", fillEnd: "#7e60f8", label: "AIHumanity" },
    llm: { fill: "#a9b5c8", fillEnd: "#a9b5c8", label: "Frontier LLMs" },
    thirdParty: { fill: "#ff9b63", fillEnd: "#ff9b63", label: "Open-source / third-party tools" },
};

export const BENCHMARKS = [
    {
        id: "frontier-llms",
        title: "AIHumanity vs. Frontier LLMs",
        eyebrow: "Facial emotion recognition",
        kind: "bar",
        unit: "%",
        rows: [
            { label: "AIHumanity Exp15 ONNX", value: 90.0, group: "aihumanity" },
            { label: "AIHumanity Exp15 CoreML", value: 90.0, group: "aihumanity" },
            { label: "GPT o4 Mini High", value: 69.0, group: "llm" },
            { label: "GPT 5.2", value: 67.0, group: "llm" },
            { label: "GPT 5 Mini", value: 66.0, group: "llm" },
            { label: "GPT o4 Mini", value: 66.0, group: "llm" },
            { label: "Gemini 3 Flash Preview", value: 63.0, group: "llm" },
            { label: "GPT 5 Nano", value: 61.0, group: "llm" },
            { label: "Claude Opus 4.5", value: 60.0, group: "llm" },
            { label: "Gemini 3 Pro Preview", value: 59.0, group: "llm" },
            { label: "Grok 4", value: 54.0, group: "llm" },
            { label: "Claude Sonnet 4.5", value: 50.0, group: "llm" },
            { label: "Grok 4.1 Fast", value: 50.0, group: "llm" },
            { label: "Claude Haiku 4.5", value: 49.0, group: "llm" },
            { label: "Imentiv AI", value: 40.0, group: "thirdParty" },
            { label: "Hume", value: 36.0, group: "thirdParty" },
        ],
        methodology: {
            intro: "Exact dominant-emotion match accuracy on AIMultiple's public 70-image facial-emotion benchmark (manojdilz/facial_emotion_detection_dataset, 10 images per class across 7 emotions).",
            datasetNote: "AIHumanity's Exp15 ONNX/CoreML scores were run locally on the identical 70-image manifest, full-image RGB 224, half-normalized preprocessing — not self-reported.",
            sourceLabel: "AIMultiple emotion AI benchmark, updated May 15, 2026",
            sourceHref: "https://aimultiple.com/emotion-ai-tools#benchmark-on-emotion-recognition",
        },
        secondaryCta: { label: "Try the live facial-expression demo", href: "/demo?tab=perception#demo-lab" },
    },
    {
        id: "open-source-models",
        title: "AIHumanity vs. Open-Source Emotion Models",
        eyebrow: "Accuracy and speed, pooled across 4 datasets",
        kind: "bar+scatter",
        unit: "%",
        rows: [
            { label: "AIHumanity Exp15 CoreML", value: 85.1, group: "aihumanity", latencyMs: 9.68 },
            { label: "EmotiEff B0 VA-MTL ONNX", value: 63.6, group: "thirdParty", latencyMs: 36.225 },
            { label: "EmotiEff B2-7 ONNX", value: 63.5, group: "thirdParty", latencyMs: 57.313 },
            { label: "EmotiEff B0 AFEW ONNX", value: 62.8, group: "thirdParty", latencyMs: 33.614 },
            { label: "EmotiEff MBF VA-MTL ONNX", value: 62.5, group: "thirdParty", latencyMs: 13.549 },
            { label: "EmotiEff B0 VGAF ONNX", value: 62.4, group: "thirdParty", latencyMs: 39.603 },
            { label: "HF trpakov ViT", value: 62.2, group: "thirdParty", latencyMs: 24.306 },
            { label: "EmotiEff MobileViT ONNX", value: 61.6, group: "thirdParty", latencyMs: 58.570 },
            { label: "EmotiEff B2-8 ONNX", value: 61.0, group: "thirdParty", latencyMs: 52.464 },
            { label: "HF Celal11 ResNet50", value: 59.2, group: "thirdParty", latencyMs: 13.698 },
            { label: "FER+ 8 ONNX", value: 52.9, group: "thirdParty", latencyMs: 12.853 },
        ],
        methodology: {
            intro: "Pooled detected accuracy across FER2013, CK+48, JAFFE, and AffectNetShort:val (13,553 overlapping prediction rows); no-face rows excluded from detected accuracy.",
            datasetNote: "Accuracy is pooled over prediction rows, not a simple average across datasets. AIHumanity Exp15 CoreML uses direct full-image RGB 224, half-normalized preprocessing.",
            sourceLabel: "Internal evaluation, local ONNX/CoreML runtimes",
            sourceHref: null,
        },
        secondaryCta: { label: "Explore the Emotion Models product", href: "/products/emotion-models" },
    },
];
